import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import mqtt from 'mqtt';

/**
 * Hook for subscribing to multiple MQTT topics and getting their live values.
 * Supports JSON field access: if a topic publishes a JSON object, you can access
 * individual fields by appending /<fieldName> to the topic path.
 * 
 * @param {Object} mqttConfig - MQTT connection configuration
 * @param {string} mqttConfig.broker - WebSocket URL of the MQTT broker
 * @param {string} mqttConfig.username - MQTT username
 * @param {string} mqttConfig.password - MQTT password
 * @param {Array<string>} topicPaths - Array of full MQTT topic paths to subscribe to.
 *   May include JSON field suffixes (e.g., "Factory/Sensor/Power/value" where "Power"
 *   is the actual MQTT topic and "value" is a JSON field).
 * @returns {Object} - { values: { [topicPath]: value }, isConnected, recentlyChanged: Set<topicPath> }
 */
const useMqttTopics = (mqttConfig, topicPaths = []) => {
  const [values, setValues] = useState({});
  const [isConnected, setIsConnected] = useState(false);
  const [recentlyChanged, setRecentlyChanged] = useState(new Set());
  
  const clientRef = useRef(null);
  const recentlyChangedTimeoutRef = useRef({});
  
  // Duration to keep a topic marked as "recently changed" for highlight effect
  const HIGHLIGHT_DURATION_MS = 1500;

  // Derive subscription topics: the original paths + parent paths for JSON field access.
  // E.g., if topicPaths includes "Power/value", we also subscribe to "Power" so we
  // receive the JSON payload and can expand it into "Power/value", "Power/quality", etc.
  const subscriptionTopics = useMemo(() => {
    const topics = new Set(topicPaths);
    topicPaths.forEach(tp => {
      if (tp.includes('#') || tp.includes('+')) return;
      const lastSlash = tp.lastIndexOf('/');
      if (lastSlash > 0) {
        topics.add(tp.substring(0, lastSlash));
      }
    });
    return Array.from(topics);
  }, [topicPaths]);

  // Extract the last key from a topic path (the actual data key we're interested in)
  const extractValueFromMessage = useCallback((msg, topicPath) => {
    // If the message is a primitive, return it directly
    if (typeof msg !== 'object' || msg === null) {
      return msg;
    }
    
    // If the message is an object, try to find a meaningful value
    // Common patterns: { value: x }, { data: x }, or first key's value
    if ('value' in msg) return msg.value;
    if ('data' in msg) return msg.data;
    
    // Get the last segment of the topic path
    const lastSegment = topicPath.split('/').pop();
    if (lastSegment && lastSegment in msg) {
      return msg[lastSegment];
    }
    
    // Return the first primitive value found, or the whole object
    const keys = Object.keys(msg);
    if (keys.length === 1) {
      const val = msg[keys[0]];
      if (typeof val !== 'object' || val === null) {
        return val;
      }
    }
    
    // Return stringified object as fallback
    return JSON.stringify(msg);
  }, []);

  // Mark a topic as recently changed (for highlight animation)
  const markAsChanged = useCallback((topicPath) => {
    setRecentlyChanged(prev => new Set([...prev, topicPath]));
    
    // Clear the highlight after duration
    if (recentlyChangedTimeoutRef.current[topicPath]) {
      clearTimeout(recentlyChangedTimeoutRef.current[topicPath]);
    }
    
    recentlyChangedTimeoutRef.current[topicPath] = setTimeout(() => {
      setRecentlyChanged(prev => {
        const next = new Set(prev);
        next.delete(topicPath);
        return next;
      });
    }, HIGHLIGHT_DURATION_MS);
  }, []);

  useEffect(() => {
    // Don't connect if no config or no topics
    if (!mqttConfig?.broker || topicPaths.length === 0) {
      return;
    }

    let isCleanedUp = false;
    const clientId = `vtec_media_factory_${Date.now()}`;
    
    const client = mqtt.connect(mqttConfig.broker, {
      username: mqttConfig.username,
      password: mqttConfig.password,
      rejectUnauthorized: false,
      connectTimeout: 30 * 1000,
      reconnectPeriod: 5 * 1000,
      keepalive: 60,
      clean: true,
      clientId: clientId,
      protocolVersion: 4,
    });
    
    clientRef.current = client;
    
    // Reset values on new connection
    setValues({});
    setIsConnected(false);
    
    // Connection timeout -- warn if we can't connect within 30s
    const connectionTimeout = setTimeout(() => {
      if (!isCleanedUp) {
        console.warn('[useMqttTopics] Connection timeout after 30s for broker:', mqttConfig.broker);
      }
    }, 30000);
    
    // On connection
    client.on('connect', () => {
      if (isCleanedUp) return;
      clearTimeout(connectionTimeout);
      setIsConnected(true);
      
      // Subscribe to all topic paths (including derived parent topics)
      subscriptionTopics.forEach((topicPath) => {
        client.subscribe(topicPath);
      });
    });
    
    // On reconnect
    client.on('reconnect', () => {
      if (isCleanedUp) return;
      console.warn('[useMqttTopics] Reconnecting to broker:', mqttConfig.broker);
    });
    
    // On disconnect
    client.on('disconnect', () => {
      if (isCleanedUp) return;
      setIsConnected(false);
    });
    
    // On offline
    client.on('offline', () => {
      if (isCleanedUp) return;
      setIsConnected(false);
    });
    
    // Message handler
    client.on('message', (topic, message) => {
      if (isCleanedUp) return;

      let msg = {};
      try {
        const msgStr = message.toString();
        try {
          msg = JSON.parse(msgStr);
        } catch {
          msg = msgStr;
        }
      } catch (error) {
        return;
      }
      
      // Check if this topic matches any of our subscribed topics
      const matchingTopic = subscriptionTopics.find(subscribedTopic => {
        if (subscribedTopic === topic) return true;
        if (subscribedTopic.endsWith('/#')) {
          const prefix = subscribedTopic.slice(0, -2);
          return topic.startsWith(prefix);
        }
        if (subscribedTopic.includes('+')) {
          const regex = new RegExp('^' + subscribedTopic.replace(/\+/g, '[^/]+') + '$');
          return regex.test(topic);
        }
        return false;
      });
      
      const isWildcard = matchingTopic && (matchingTopic.includes('#') || matchingTopic.includes('+'));
      const topicKey = isWildcard ? topic : (matchingTopic || topic);
      
      const value = extractValueFromMessage(msg, topic);
      
      setValues(prev => {
        const updates = { [topicKey]: value };
        const changedKeys = [];

        if (prev[topicKey] !== value) {
          changedKeys.push(topicKey);
        }

        // JSON field expansion: when payload is a JSON object, create entries
        // for each first-level field as topic/fieldName virtual sub-topics
        if (typeof msg === 'object' && msg !== null && !Array.isArray(msg)) {
          for (const [key, val] of Object.entries(msg)) {
            const expandedKey = `${topic}/${key}`;
            const expandedVal = (typeof val === 'object' && val !== null)
              ? JSON.stringify(val)
              : val;
            if (prev[expandedKey] !== expandedVal) {
              changedKeys.push(expandedKey);
            }
            updates[expandedKey] = expandedVal;
          }
        }

        if (changedKeys.length > 0 && !isCleanedUp) {
          Promise.resolve().then(() => {
            if (!isCleanedUp) {
              changedKeys.forEach(k => markAsChanged(k));
            }
          });
        }

        return { ...prev, ...updates };
      });
    });
    
    // Handle connection errors
    client.on('error', (err) => {
      if (isCleanedUp) return;
      console.warn('[useMqttTopics] Connection error:', err?.message || err);
    });
    
    // Cleanup on unmount or config change
    return () => {
      isCleanedUp = true;
      clearTimeout(connectionTimeout);
      
      // Clear all highlight timeouts
      Object.values(recentlyChangedTimeoutRef.current).forEach(clearTimeout);
      recentlyChangedTimeoutRef.current = {};
      
      if (clientRef.current) {
        subscriptionTopics.forEach(topicPath => {
          clientRef.current.unsubscribe(topicPath);
        });
        clientRef.current.end();
        clientRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mqttConfig?.broker, mqttConfig?.username, mqttConfig?.password, JSON.stringify(subscriptionTopics)]);

  return { values, isConnected, recentlyChanged };
};

export default useMqttTopics;

